import { Radio, Tag } from 'antd';
import React from 'react';
import {
    EStatusOfService,
    TCarInService,
    TServiceDetail,
    StatusOfServiceColorMapping,
} from '../start-work-order/type';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import classNames from 'classnames';
import { AutoTrackButton } from '../../components/button';

type ServicesActionsPageProps = {
    carInServiceSelected: TCarInService | undefined;
    service: TServiceDetail;
    oneService: boolean;
    size?: SizeType;
    mobileMode: boolean;
    forceReload?: boolean;
    onUpdateStatus: (
        status: EStatusOfService,
        serviceSelection: TServiceDetail,
        task: TCarInService | undefined,
        isForceReload: boolean
    ) => void;
};

export const ServicesActionsPage: React.FC<ServicesActionsPageProps> = ({
    service,
    size,
    oneService,
    mobileMode,
    onUpdateStatus,
    carInServiceSelected,
    forceReload,
}) => {
    const [actionOptions, setActionOptions] = React.useState<
        { label: string; value: EStatusOfService }[]
    >([]);
    const [value, setValue] = React.useState<EStatusOfService | undefined>();
    const [currentService, setCurrentService] = React.useState<
        TServiceDetail | undefined
    >(undefined);

    React.useEffect(() => {
        setCurrentService(service);
    }, [service]);

    React.useEffect(() => {
        if (
            !currentService?.serviceDetailStatus ||
            currentService?.serviceDetailStatus === undefined
        ) {
            setActionOptions([
                { label: 'Start', value: EStatusOfService.START },
            ]);
            return;
        }
        if (currentService?.serviceDetailStatus === EStatusOfService.START) {
            setActionOptions([
                { label: 'Pause', value: EStatusOfService.PAUSE },
                { label: 'Done', value: EStatusOfService.DONE },
            ]);
        }
        if (currentService?.serviceDetailStatus === EStatusOfService.PAUSE) {
            setActionOptions([
                { label: 'UnPause', value: EStatusOfService.UNPAUSE },
                { label: 'Done', value: EStatusOfService.DONE },
            ]);
        }
        if (currentService?.serviceDetailStatus === EStatusOfService.UNPAUSE) {
            setActionOptions([
                { label: 'Pause', value: EStatusOfService.PAUSE },
                { label: 'Done', value: EStatusOfService.DONE },
            ]);
        }
        if (currentService?.serviceDetailStatus === EStatusOfService.DONE) {
            setActionOptions([]);
        }
    }, [currentService]);

    const onChange = (e: any) => {
        setValue(e.target.value);
        const newService = {
            ...currentService,
        } as TServiceDetail;
        const isReloadData = forceReload
            ? forceReload
            : oneService || mobileMode
              ? true
              : false;
        onUpdateStatus(
            e.target.value,
            newService,
            carInServiceSelected,
            isReloadData
        );
    };

    const hasUserAssigned = React.useMemo(() => {
        if (!currentService) return false;
        if (!currentService.user) return false;
        return (
            currentService?.user !== undefined &&
            Object.keys(currentService?.user).length > 0
        );
    }, [currentService?.user]);

    const doneService = React.useMemo(() => {
        return currentService?.serviceDetailStatus === EStatusOfService.DONE;
    }, [currentService?.serviceDetailStatus]);

    const renderNoneMobile = React.useMemo(() => {
        if (mobileMode) return null;
        return (
            <>
                {hasUserAssigned && !doneService && (
                    <Radio.Group
                        size={oneService ? 'large' : (size ?? 'small')}
                        options={actionOptions}
                        onChange={onChange}
                        value={value}
                        buttonStyle="solid"
                        optionType="button"
                    />
                )}
                {doneService && <Tag color="green">Done</Tag>}
            </>
        );
    }, [
        mobileMode,
        oneService,
        doneService,
        hasUserAssigned,
        actionOptions,
        value,
        carInServiceSelected,
        forceReload,
    ]);

    const renderMobile = React.useMemo(() => {
        if (!mobileMode) return null;
        return (
            <div
                className={classNames([
                    'grid w-full',
                    {
                        'grid-cols-2 gap-2': actionOptions.length > 1,
                    },
                    {
                        'grid-cols-1 pt-0': actionOptions.length === 1,
                    },
                ])}
            >
                {actionOptions.map((item, index) => {
                    return (
                        <AutoTrackButton
                            btnSize="md"
                            classess="w-full"
                            style={{
                                height: '40px',
                                width: '100%',
                                backgroundColor:
                                    StatusOfServiceColorMapping[item.value],
                            }}
                            key={index}
                            className={classNames([
                                'flex-1 border border-gray-300 rounded-md py-1',
                                {
                                    'mr-1': index === 0,
                                },
                                {
                                    'ml-1': index === actionOptions.length - 1,
                                },
                            ])}
                            onClick={() => {
                                onChange({
                                    target: {
                                        value: item.value,
                                    },
                                });
                            }}
                        >
                            {item.label}
                        </AutoTrackButton>
                    );
                })}
            </div>
        );
    }, [mobileMode, forceReload, actionOptions, value, carInServiceSelected]);

    return !hasUserAssigned && !doneService ? null : (
        <div
            className={classNames([
                'flex flex-col items-end justify-end relative',
                {
                    'min-w-[120px]': !mobileMode,
                },
                {
                    'min-w-[100%] w-full items-center justify-center':
                        mobileMode,
                },
            ])}
        >
            {renderMobile}
            {renderNoneMobile}
        </div>
    );
};
